/**
 * LITERAL light values of the extended role groups (dark mode, KAN-304 step 2 fix-up).
 *
 * These cover the leftover hand-typed colours the step-2 sweep found (success/blue/grey/red families, soft
 * tints, hairlines, chart hues, auth panel, industry accents, gradient washes). Light = today's exact value;
 * `themePalettes.extended.js` carries the Graphite twin of every key. Never imported by UI code — the UI reads
 * them through `semanticTokens.js` (`var(--cvx-<group>-<key>)`).
 */

import { slate, accent, amber } from './primitiveTokens';

/** Coloured TEXT / ICON / DOT hues that no base role matches exactly. */
export const ink = {
  successVivid: '#16a34a',
  successDot: '#22c55e',
  successText: '#15803d',
  successInk: '#166534',
  successDeep: '#065f46',
  blueVivid: '#2563eb',
  blueBright: '#3b82f6',
  blueLight: '#60a5fa',
  blueMid: '#4a8dd4',
  blueDeep: '#1e40af',
  navyDeep: '#1e3a8a',
  blueLink: '#0a66d8',
  indigoBright: '#6366f1',
  dangerDeep: '#991b1b',
  dangerDeeper: '#7f1d1d',
  dangerVivid: '#ef4444',
  roseBright: '#fb7185',
  roseStrong: '#f43f5e',
  orangeDeep: '#9a3412',
  tealVivid: '#0d9488',
  tealBright: '#14b8a6',
  violetVivid: '#7c3aed',
  amberBright: '#fbbf24',
  gray400: '#9ca3af',
  gray700: '#374151',
  grayFaint: '#b0b8c4',
  slate300: '#cbd5e1',
  ios: '#1C1C1E',
  iosBlue: '#007AFF',
  alpha62: 'rgba(0,0,0,0.62)',
  alpha56: 'rgba(0,0,0,0.56)',
  alpha48: 'rgba(0,0,0,0.48)',
};

/** Soft fills (washes, pills, hover grounds). */
export const tint = {
  /** Paper plate a company logo sits on in the app (dark-ink logos stay visible in Graphite). Never on PDFs/emails. */
  logoPlate: '#ffffff',
  /** Tile a logo sits in on the invoice-look preview: the muted grey in Light (= bg.surfaceMuted), a white plate in Graphite. */
  logoTile: '#f1f5f9',
  /** Resting fill of a 2FA code cell (= brand.primaryMuted in Light). */
  otpCell: '#edf3fc',
  blue50: '#eff6ff',
  blue100: '#dbeafe',
  blueFaint: 'rgba(59, 130, 246, 0.06)',
  blueSoft: 'rgba(59, 130, 246, 0.08)',
  panel: '#edf3fb',
  panelFaint: '#eef4fc',
  indigo50: '#eef2ff',
  indigoPill: 'rgba(99, 102, 241, 0.12)',
  grey50: '#f9fafb',
  grey100: '#f0f4f8',
  slate25: '#fafbfc',
  green50: '#f0fdf4',
  green100: '#dcfce7',
  rose50: '#fff1f2',
  rose100: '#fae3e3',
  orange50: '#fff7ed',
  highlight: 'rgba(250, 204, 21, 0.35)',
  glass: 'rgba(255, 255, 255, 0.72)',
  ink04: 'rgba(0,0,0,0.04)',
  ink05: 'rgba(0,0,0,0.05)',
};

/** Borders and hairlines drawn with a fixed colour or dark-ink alpha (they vanish on dark otherwise). */
export const edge = {
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  slate400: '#94a3b8',
  input: '#dfe4ea',
  card: '#e9edf2',
  soft: '#eef1f5',
  steel: '#a8bdd1',
  open: '#e3ecf8',
  blue: '#dbe4ff',
  rose: '#fca5a5',
  navy: '#1e3a8a',
  hair06: 'rgba(15, 23, 42, 0.06)',
  hair08: 'rgba(15, 23, 42, 0.08)',
  black05: 'rgba(0,0,0,0.05)',
  black06: 'rgba(0,0,0,0.06)',
  glass: 'rgba(255, 255, 255, 0.6)',
  /** AI composer focus ring (accent 400 at 50%). */
  focusAi: 'rgba(74, 141, 212, 0.5)',
};

/** Gradient washes on chrome — gradients in Light, FLAT in Graphite (nothing may look "AI"). */
export const wash = {
  followUp: 'linear-gradient(135deg, #edf3fc 0%, #eff6ff 50%, #f0f4ff 100%)',
  hero: `linear-gradient(135deg, ${accent[50]}, #eff6ff)`,
  warn: `linear-gradient(135deg, ${amber[50]}, #fff7ed)`,
  briefing: `linear-gradient(135deg, #eff6ff 0%, ${accent[100]} 100%)`,
  briefingStrong: `linear-gradient(135deg, #dbeafe 0%, ${accent[100]} 100%)`,
  tile: `linear-gradient(180deg, #eef4fc 0%, ${slate[50]} 100%)`,
  brandFill: `linear-gradient(135deg, ${accent[700]}, #1e40af)`,
  aiFill: `linear-gradient(148deg, ${accent[800]} 0%, ${accent[700]} 48%, ${accent[500]} 100%)`,
  aiBorder: `linear-gradient(135deg, ${accent[900]}, ${accent[500]})`,
  aiButton: `linear-gradient(148deg, ${accent[800]}, ${accent[700]})`,
  authButton: 'linear-gradient(160deg, #2670bd 0%, #1c5aa5 55%, #174d8e 100%)',
  /** Full-page pre-auth / access-status ground (Light = bg.surfaceMuted, slate-100; Dark = the black canvas like sign-in). */
  authPage: slate[100],
  authGround: 'linear-gradient(180deg, #eaf1fa 0%, #f1f5fb 45%, #f8fafd 100%)',
  authPanel: '#faf8f5',
  authPanelEdge: '#f0ece6',
};

/** Chart hues (pipeline funnel, insights tiles). Graphite keeps the hue family, lifted to ≥3:1 on the surface. */
export const chart = {
  drafts: '#cbd5e1',
  sent: '#0ea5e9',
  paid: '#10b981',
  overdue: '#d97706',
  sentAccent: '#0284c7',
  sentBg: '#e0f2fe',
  purple: '#7c3aed',
  purpleBg: '#f5f3ff',
  pink: '#ec4899',
  pinkBg: '#fdf2f8',
};

/** Industry accent triples (text hue, soft fill, soft border) + focus ring. */
export const industry = {
  poolAccent: '#0f766e', poolSoft: '#f0fdfa', poolBorder: '#99f6e4', poolFocus: 'rgba(15, 118, 110, 0.35)',
  hvacAccent: '#0369a1', hvacSoft: '#f0f9ff', hvacBorder: '#bae6fd', hvacFocus: 'rgba(3, 105, 161, 0.35)',
  cleaningAccent: '#15803d', cleaningSoft: '#f0fdf4', cleaningBorder: '#bbf7d0', cleaningFocus: 'rgba(21, 128, 61, 0.35)',
  landscapingAccent: '#4d7c0f', landscapingSoft: '#f7fee7', landscapingBorder: '#d9f99d', landscapingFocus: 'rgba(77, 124, 15, 0.35)',
  pestAccent: '#b45309', pestSoft: '#fffbeb', pestBorder: '#fde68a', pestFocus: 'rgba(180, 83, 9, 0.35)',
  otherAccent: '#475569', otherSoft: '#f8fafc', otherBorder: '#e2e8f0', otherFocus: 'rgba(71, 85, 105, 0.35)',
  defaultFocus: 'rgba(21, 42, 69, 0.35)',
  genericFocus: 'rgba(35, 69, 110, 0.35)',
};

export const extended = { ink, tint, edge, wash, chart, industry };
export const EXTENDED_GROUPS = Object.keys(extended);
